import { Component, inject, OnInit, Input, Output, EventEmitter, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { PageService, PageType, SettingsService } from '@gallaghercp/core-frontend';
import { APP_CONFIG } from '@core-app/app.config';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'core-top-nav-utilities',
    templateUrl: './top-nav-utilities.component.html',
    styleUrls: ['./top-nav-utilities.component.scss'],
    standalone: false,
})
export class TopNavUtilitiesComponent implements OnInit {
    @Input() position: 'above' | 'below' | 'mobile' = 'above';
    @Output() clickLink = new EventEmitter<void>();
    private pageService = inject(PageService);
    private router = inject(Router);
    private settings = inject(SettingsService);
    private destroyRef = inject(DestroyRef);

    utilitiesPages$?: Observable<PageType[]>;
    darkMode = false;

    get bgColor(): string {
        const config = (APP_CONFIG as any).theme?.navigation;
        if (this.position === 'above') {
            if (this.darkMode) {
                return config?.utilitiesAboveDarkBgColor || '#1c1b1f';
            }
            return config?.utilitiesAboveBgColor || '#f0f1f2';
        } else {
            if (this.darkMode) {
                return config?.utilitiesBelowDarkBgColor || '#1c1b1f';
            }
            return config?.utilitiesBelowBgColor || '#f0f1f2';
        }
    }

    ngOnInit(): void {
        this.settings
            .isDarkMode()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((state: any) => {
                this.darkMode = state;
            });

        this.utilitiesPages$ = this.pageService
            .getPages()
            .pipe(map(pages => this.filterUtilitiesPages(pages as PageType[])));
    }

    private filterUtilitiesPages(pages: PageType[]): PageType[] {
        const utilPages: PageType[] = [];
        const pageIds = new Set<number>();

        const checkPage = (page: PageType) => {
            let isUtilPageAdded = false;
            if (page.meta) {
                const showAbove = String(page.meta['showInUtilitiesNavbarAbove']) === 'true';
                const showBelow = String(page.meta['showInUtilitiesNavbarBelow']) === 'true';

                if (
                    (this.position === 'above' && showAbove) ||
                    (this.position === 'below' && showBelow) ||
                    (this.position === 'mobile' && (showAbove || showBelow))
                ) {
                    if (!pageIds.has(page.id)) {
                        pageIds.add(page.id);
                        utilPages.push(page);
                        isUtilPageAdded = true;
                    }
                }
            }
            if (!isUtilPageAdded && page.children && page.children.length > 0) {
                page.children.forEach(child => checkPage(child));
            }
        };

        pages.forEach(p => checkPage(p));
        return utilPages.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    }

    navigateToPage(page: PageType): void {
        this.clickLink.emit();
        if (page.meta?.redirect) {
            window.open(page.meta.redirect, '_blank');
        } else {
            this.router.navigate(['/', page.url]);
        }
    }

    showInMenu(page: any, type: string): boolean {
        if (type === 'no-child') {
            return (
                page.meta?.showInMenu == 'true' ||
                page.meta?.showInMenu == "'true'" ||
                page.meta?.showInMenu == true ||
                page.meta?.showInUtilitiesNavbarAbove == 'true' ||
                page.meta?.showInUtilitiesNavbarAbove == true ||
                page.meta?.showInUtilitiesNavbarBelow == 'true' ||
                page.meta?.showInUtilitiesNavbarBelow == true
            );
        }
        return false;
    }
}
